import React from 'react';
import { FileText, Calendar, User, Package } from 'lucide-react';
import { formatShortDate } from '../utils/dateUtils';

// Local interface - matches backend PayoutService.InvoiceRevenueDetail
interface InvoiceRevenueDetail {
  invoiceId: string;
  invoiceNumber: string;
  totalAmount: number;
  paidAt: Date;
  physicianName: string;
  items: Array<{
    description: string;
    quantity: number;
    productCost?: number; // Floor/base price — admin only; recipients get their own cost here
    amCost: number;       // AM's cost (catalog price)
    amPrice: number;      // AM's sell price (invoice price)
    unitMargin: number;   // Per-unit profit
    amPayout: number;     // Total profit
  }>;
  totalAmPayout: number;
}

interface InvoiceBreakdownProps {
  invoices: InvoiceRevenueDetail[];
  title?: string;
  showRevenue?: boolean; // Whether to show AM payout (for admins/distributors)
  // The pharmacy floor column. Admin-only: the API sends recipients their own
  // cost in productCost, which would just repeat the AM Cost column.
  showProductCost?: boolean;
}

const formatCurrency = (amount: number) => {
  if (isNaN(amount) || amount === undefined || amount === null) {
    return '$0.00';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

export const InvoiceBreakdown: React.FC<InvoiceBreakdownProps> = ({
  invoices,
  title = "Invoice Breakdown",
  showRevenue = true,
  showProductCost = true
}) => {
  if (!invoices || invoices.length === 0) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-500">No invoices found for this period</p>
      </div>
    );
  }

  const totalPayout = invoices.reduce((sum, invoice) => sum + (invoice.totalAmPayout || 0), 0);
  const totalInvoiceAmount = invoices.reduce((sum, invoice) => sum + (invoice.totalAmount || 0), 0);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          {title}
        </h3>
        <div className="text-sm text-gray-500">
          {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Summary */}
      <div className="bg-blue-50 rounded-lg p-4 grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-blue-600 font-medium">Total Invoice Amount</p>
          <p className="text-xl font-bold text-blue-900">{formatCurrency(totalInvoiceAmount)}</p>
        </div>
        {showRevenue && (
          <div>
            <p className="text-sm text-green-600 font-medium">Account Manager Payout</p>
            <p className="text-xl font-bold text-green-900">{formatCurrency(totalPayout)}</p>
          </div>
        )}
      </div>

      {/* Invoice List */}
      <div className="space-y-3">
        {invoices.map((invoice) => (
          <div key={invoice.invoiceId} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
            {/* Invoice Header */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="font-medium text-gray-900">{invoice.invoiceNumber}</span>
                  <span className="text-sm text-gray-500">•</span>
                  <span className="text-sm font-medium text-gray-900">{formatCurrency(invoice.totalAmount)}</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {invoice.physicianName}
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {formatShortDate(invoice.paidAt)}
                  </div>
                </div>
              </div>
              {showRevenue && (
                <div className="text-right">
                  <p className="text-sm text-gray-500">AM Payout</p>
                  <p className="font-semibold text-green-600">{formatCurrency(invoice.totalAmPayout)}</p>
                </div>
              )}
            </div>

            {/* Invoice Items */}
            <div className="space-y-2">
              {invoice.items.map((item, index) => (
                <div key={index} className="bg-gray-50 rounded p-3 text-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Package className="w-3 h-3 text-gray-400" />
                        <span className="font-medium text-gray-900">{item.description}</span>
                        <span className="text-gray-500">× {item.quantity}</span>
                      </div>
                      <div className={`text-gray-600 text-xs grid ${showProductCost ? 'grid-cols-4' : 'grid-cols-3'} gap-2 mt-1`}>
                        {showProductCost && (
                          <div>
                            <span className="text-gray-400">Product Cost:</span>
                            <span className="ml-1">{formatCurrency(item.productCost ?? 0)}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-400">AM Cost:</span>
                          <span className="ml-1">{formatCurrency(item.amCost)}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">AM Price:</span>
                          <span className="ml-1">{formatCurrency(item.amPrice)}</span>
                        </div>
                        <div>
                          <span className="text-gray-400">Unit Margin:</span>
                          <span className="ml-1 text-green-600 font-medium">{formatCurrency(item.unitMargin)}</span>
                        </div>
                      </div>
                    </div>
                    {showRevenue && (
                      <div className="text-right ml-4">
                        <p className="text-xs text-gray-500">Item Payout</p>
                        <p className="font-medium text-green-600">{formatCurrency(item.amPayout)}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
